import { CommonModule, NgIf } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    Inject,
    OnInit
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import { ApiError, HttpService } from '../../../services/http.service';
import { UserProfileService } from '../../../services/user-profile.service';
import { InputComponent } from '../../from-input/input.component';
import { CustomToastComponent } from '../../toast/custom-toast.component';

interface APIResponse {
    id: string;
    name: string;
    position: string;
    fullName: string;
    email: string;
    phoneNumber: string;
}

interface ApiDataResponse {
    status: boolean;
    message: string;
    data: APIResponse;
}

@Component({
    selector: 'app-contact-dialog',
    standalone: true,
    templateUrl: './contact-details-dialog.component.html',
    styleUrls: ['./contact-details-dialog.component.scss'],
    imports: [CommonModule, NgIf, ReactiveFormsModule, InputComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ContactDialogComponent implements OnInit {
    form!: FormGroup;
    loading = false;
    isEdit: boolean = false;
    userData: APIResponse | null = null;
    schoolOptions: any[] = [];
    showSchoolField = true;
    selectedSchoolID: any = ""
    viewMode: boolean = false
    isProcessing = false;
    readonly customErrorMessages = {
        name: {
            required: 'Please enter a contact name.',
        },
        position: {
            required: 'Please enter a position.',
        },
        email: {
            required: 'Please enter a email address.',
            email: 'Please enter a valid email.',
        },
        phoneNumber: {
            required: 'Please enter a phone number.',
            pattern: 'Please enter a valid phone number.'
        },
    };

    constructor(
        private fb: FormBuilder,
        private httpService: HttpService,
        private snackBar: MatSnackBar,
        private cdr: ChangeDetectorRef,
        public dialogRef: MatDialogRef<ContactDialogComponent>,
        private dialog: MatDialog,
        private userProfileService: UserProfileService,
        @Inject(MAT_DIALOG_DATA) public data: { id: string, schoolID: string; initialData?: APIResponse; EditMode: boolean, ViewMode: boolean }
    ) {
        this.initForm();
        this.isEdit = data?.EditMode || false;
        this.selectedSchoolID = data?.schoolID || ""
        this.viewMode = data?.ViewMode || false
    }

    ngOnInit(): void {
        if (this.isEdit && this.data?.initialData || this.viewMode) {
            this.patchFormWithInitialData();
        }
    }

    private initForm(): void {
        this.form = this.fb.group({
            name: ['', Validators.required],
            position: ['', Validators.required],
            email: ['', [Validators.required, Validators.pattern(
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
            )]],
            phoneNumber: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^(?:0[47]\d{8}|\+61 ?[47]\d{8})$/)
                ]
            ],
        });
    }

    private patchFormWithInitialData(): void {
        const initialData = this.data.initialData;

        if (initialData) {
            this.form.patchValue({
                name: initialData.name || '',
                position: initialData.position || '',
                email: initialData.email || '',
                phoneNumber: (initialData as any)['phone number'] ?? (initialData as any).phoneNumber ?? (initialData as any).phoneNumber,
            });

            this.cdr.detectChanges();
        }
    }

    onCancel(): void {
        this.dialogRef.close(false);
    }

    onSave(): void {
        // Prevent multiple clicks
        if (this.loading || this.isProcessing || this.viewMode) {
            return;
        }

        // Set flags to prevent multiple submissions
        this.isProcessing = true;
        this.loading = true;

        // Mark all fields as touched to show validation errors
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.loading = false;
            this.isProcessing = false;
            return;
        }

        // Prepare payload
        const payload = {
            name: this.form.get('name')?.value?.trim(),
            email: this.form.get('email')?.value?.trim(),
            phone: this.form.get('phoneNumber')?.value?.trim(),
            position: this.form.get('position')?.value?.trim(),
        };

        // Determine the API endpoint
        const endpoint = this.isEdit
            ? `schools/${this.selectedSchoolID}/contacts/${this.data.initialData?.id}`
            : `schools/${this.selectedSchoolID}/contacts`;

        // Make the API call
        const request = this.isEdit
            ? this.httpService.put<ApiDataResponse>(endpoint, payload)
            : this.httpService.post<ApiDataResponse>(endpoint, payload);

        // Execute the request
        request.pipe(
            finalize(() => {
                // Reset flags when the request completes (success or error)
                this.loading = false;
                this.isProcessing = false;
                this.form.enable();
            })
        ).subscribe({
            next: (response) => this.handleSubmitResponse(response),
            error: (err: ApiError) => {
                // Error handling
                this.loading = false;
                this.isProcessing = false;

                if (err?.errors && typeof err.errors === 'object') {
                    Object.values(err.errors).forEach((fieldErrors: any) => {
                        if (Array.isArray(fieldErrors)) {
                            fieldErrors.forEach(message => {
                                this.showErrorToast(message);
                            });
                        } else if (typeof fieldErrors === 'string') {
                            this.showErrorToast(fieldErrors);
                        }
                    });
                    return;
                }

                this.showErrorToast(err?.message || 'Something went wrong. Please try again later.');
            }
        });
    }

    private handleSubmitResponse(response: ApiDataResponse): void {
        if (response.status) {
            const message = this.isEdit ? 'Contact updated successfully' : 'Contact created successfully';
            this.dialogRef.close({ success: true, data: response.data });
            this.showSuccessToast(response.message || message);
        } else {
            this.showErrorToast(response.message || 'Something went wrong. Please try again later.');
        }

        // Flags are reset in finalize() - no need to reset here
    }

    private showSuccessToast(message: string): void {
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'success-toast']
        });
    }

    private showErrorToast(message: string | null | undefined): void {
        if (!message) return;
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'error-toast']
        });
    }
}